import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import feathers from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import io from 'socket.io-client';
import auth from '@feathersjs/authentication-client';
import { updateServerConnection } from 'reducers/ServerConnectionReducer';
import { getAccessPermission } from 'reducers/PermissionsReducer';
import { setWebsocketConnection } from './reducers/WebsocketReducer';
import { setUserAuthenticated, setUserData, logOut } from './reducers/UserReducer';
import * as serviceWorker from './serviceWorker';
import { notifyPanel, notify } from './reducers/NotifierReducer';

// Layout Blueprints
import { MinimalLayout, CadLayout } from './layout-blueprints';

// Pages
import PageLogin from './pages/PageLogin';
import Page404 from './pages/Page404';
import ResetPassword from 'pages/ResetPassword';
import ForcePasswordReset from 'pages/ForcePasswordReset';
import routeDefinitions, { defaultUrl } from './routeDefinitions';
import Loading from 'pages/Loading';
import NotAuthorized from 'pages/NotAuthorized';
import { registerAppUpdate } from 'reducers/AppInfoReducer';
import Cookies from 'js-cookie';
import { getRestClient } from './restClient';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { registerGlobalUserStampHook } from 'userStampHook';

let reloadNeeded = false;
let registration = false;
let noPermissionShown = false;

const ProtectedRoute = ({ component: Component, isAuthenticated, ...rest }) => {
  if (!isAuthenticated && reloadNeeded) updateApp(registration);
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated === true ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: '/Login', state: { from: props.location } }} />
        )
      }
    />
  );
};

const LoginRoute = ({ component: Component, isAuthenticated, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated === true ? (
          <Redirect to={{ pathname: defaultUrl, state: { from: props.location } }} />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
};

const Routes = (props) => {
  const {
    setWebsocketConnection,
    setUserAuthenticated,
    setUserData,
    config,
    user,
    permissions,
    userData,
    websocket,
  } = props;

  const { isAuthenticated, passExpirationDays, showPassChange } = user;
  const cadPermission = getAccessPermission('modules', 'CAD', 'any');
  const isLoaded = config.loaded && permissions?.loaded && config.dictionaryLoaded;
  const [cadAccessControl, setCadAccessControl] = useState(true);

  useEffect(() => {
    if (!websocket || !isAuthenticated) return;
    const cdrService = websocket.service('cdr');
    cdrService.get({ Path: 'Settings.Application.CAD', CDRKey: 'AccessControl' })
      .then(data => {
        const item = data?.[0];
        setCadAccessControl(!item || item.ValueBoolean == null ? true : Boolean(item.ValueBoolean));
      })
      .catch((err) => {
        console.error("CDR fetch error:", err);
        setCadAccessControl(true);
      });
  }, [websocket, isAuthenticated]);

  useEffect(() => {
    serviceWorker.register({
      onSuccess: () => console.log('service worker registered!'),
      onUpdate: () => {
        sessionStorage.setItem('pending_update', 'true');
      },
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const hasUpdate = sessionStorage.getItem('pending_update');
    if (hasUpdate !== 'true') return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      const waitingServiceWorker = reg.waiting;
      if (!waitingServiceWorker) return;
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
      waitingServiceWorker.onstatechange = (event) => {
        if (event.target.state === 'activated') {
          sessionStorage.removeItem('pending_update');
          window.location.reload();
        }
      };
    });
  }, [isAuthenticated]);

  useEffect(() => {
    async function establishConnection() {
      const client = feathers();
      const socket = io(window.__RUNTIME_CONFIG__.REACT_APP_API_URL, {
        transports: ['websocket', 'polling'],
        perMessageDeflate: false,
        timeout: 20000,
        requestTimeout: 20000,
      });
      client.configure(socketio(socket));
      client.configure(auth({ storageKey: 'auth' }));

      registerGlobalUserStampHook(client);

      socket.on('disconnect', () => props.updateServerConnection(false));
      socket.on('connect', () => props.updateServerConnection(true));
      try {
        const auth = Cookies.get('auth');
        if (auth) {
          window.localStorage.setItem('auth', auth);
          Cookies.remove('auth');
        }
        const user = await client.reAuthenticate();
        setWebsocketConnection(client);
        getRestClient();
        // setUserData(user);
        // setUserAuthenticated(true);
      } catch (error) {
        if (error.code === 401) {
          setUserAuthenticated(false);
          setUserData(null);
          setWebsocketConnection(client);
        }
        setWebsocketConnection(client);
      }

      return () => client.disconnect();
    }
    if (!websocket) {
      establishConnection();
    }
    // eslint-disable-next-line
  }, [websocket, setWebsocketConnection, setUserAuthenticated]);

  const refreshToken = async () => {
    try {
      props.showSpinner();
      const currentToken = localStorage.getItem('auth');
      const response = await websocket.service('authentication').create({
        strategy: 'jwt',
        accessToken: currentToken,
        isRenewToken: true,
        AppName: 'CAD',
      });
      localStorage.setItem('auth', response.accessToken);
      websocket.set('authentication', response);
      setUserData(response);
      setUserAuthenticated(true);
      props.notify('Session extended!', 'success');
    } catch (error) {
      console.error('Refresh Failed', error);
      setUserAuthenticated(false);
    } finally {
      props.hideSpinner();
    }
  };

  const showWarning = (expiryTime) => {
    const expiryTimeText = new Date(expiryTime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    props.notifyPanel(
      {
        title: 'Session Expiring Soon!',
        message: `Your session will expire at ${expiryTimeText}. Click CONTINUE to stay signed in.`,
        position: 'tc',
        level: 'warning',
        autoDismiss: props.sessionWarningMinutes * 60,
        action: {
          label: 'CONTINUE',
          callback: refreshToken,
        },
      },
      'warning'
    );
  };

  React.useEffect(() => {
    if (!isAuthenticated || !userData || !props.sessionWarningEnabled) return;
    const payload = userData?.authentication?.payload || userData?.payload;
    const exp = payload?.exp;
    if (!exp) return;
    const expiryTime = exp * 1000;
    const warningSeconds = Number(props.sessionWarningMinutes) * 60;
    const now = Date.now();
    const delay = expiryTime - now - warningSeconds * 1000;

    if (delay <= 0) {
      if (now < expiryTime) {
        showWarning(expiryTime);
      }
      return;
    }
    const timer = setTimeout(() => {
      showWarning(expiryTime);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [isAuthenticated, userData, props.sessionWarningEnabled]);

  const renderRoutes = () =>
    routeDefinitions.map((route, key) => {
      let Layout = isLoaded ? route.layout : MinimalLayout;
      let component = isLoaded ? route.component : Loading;

      if (isLoaded && (!cadPermission || !cadAccessControl)) {
        component = NotAuthorized;
        Layout = MinimalLayout;
        if (!noPermissionShown) {
          noPermissionShown = true;
          props.notify('No sufficient permissions to run this application', 'error');
          props.logOut();
        }
      }

      return (
        <Route path={[route.url]} key={key}>
          <Layout>
            <ProtectedRoute
              path={route.url}
              component={component}
              isAuthenticated={isAuthenticated}
            />
          </Layout>
        </Route>
      );
    });

  return (
    <Switch>
      <Redirect exact from="/" to="/Login" />
      <Route path={['/Login']}>
        <MinimalLayout>
          <LoginRoute path="/Login" component={PageLogin} isAuthenticated={isAuthenticated} />
        </MinimalLayout>
      </Route>
      <Route path={['/Reset-password']}>
        <MinimalLayout>
          <ResetPassword isAuthenticated={isAuthenticated} />
        </MinimalLayout>
      </Route>
      {passExpirationDays !== null && !showPassChange && renderRoutes()}
      {showPassChange && (
        <Route path={['/']}>
          <MinimalLayout>
            <ForcePasswordReset passExpirationDays={passExpirationDays} />
          </MinimalLayout>
        </Route>
      )}
      {passExpirationDays === null && (
        <Route path={['/']}>
          <MinimalLayout>
            <ProtectedRoute component={Loading} isAuthenticated={isAuthenticated} />
          </MinimalLayout>
        </Route>
      )}
      <Route>
        <CadLayout>
          <ProtectedRoute component={Page404} isAuthenticated={isAuthenticated} />
        </CadLayout>
      </Route>
    </Switch>
  );
};

const mapStateToProps = (state) => ({
  network: state.offline,
  user: state.user,
  config: state.config,
  permissions: state.permissions,
  userData: state.user.userData,
  websocket: state.websocket.websocket,
  sessionWarningEnabled: state.config.sessionWarningEnabled,
  sessionWarningMinutes: state.config.sessionWarningMinutes,
});

export default connect(mapStateToProps, {
  setWebsocketConnection,
  setUserAuthenticated,
  setUserData,
  notifyPanel,
  registerAppUpdate,
  notify,
  logOut,
  updateServerConnection,
  showSpinner,
  hideSpinner,
})(Routes);
